import React, { useRef, useState } from 'react';
import {
  HiOutlineDownload,
  HiOutlineExclamation,
  HiOutlineExternalLink,
} from 'react-icons/hi';

import ToastNotification from '../../components/ToastNotification';
import { useOperatorToken } from '../../hooks/useOperatorToken';
import {
  fetchTicketFileDownload,
  regenerateTicketInvoice,
  replaceTicketFlipBook,
  restoreTicketFile,
  updateTicketShipTo,
  uploadTicketFile,
} from './api';
import {
  flipBookReplaceErrorMessage,
  formatDateTime,
  formatShipTo,
  isOperatorUnverified,
  printFileClientError,
  printFileErrorMessage,
  shipToErrorMessage,
} from './helpers';
import { formatMessage, printShopStrings } from './strings';
import {
  BookShipTo,
  TicketBookLinks,
  TicketDetail,
  TicketFileKind,
  TicketFileStatus,
} from './types';

/**
 * The two panels a BOOK print order needs and a mailing has no equivalent for
 * (ADR-002, ADR-003):
 *
 *  - the facts the operator acts on — the order number both sides quote, how
 *    many copies, and the single address they ship to;
 *  - the three print files, fetched as short-lived signed URLs, followed by
 *    the book's flip book and lead-capture site (public pages, plain links).
 *
 * Kept out of `TicketDetail` because the mailing path links its PDFs directly:
 * a MOD asset URL is reachable as-is, while a book's objects live in a private
 * bucket and must be signed per click (ADR-006). Two genuinely different
 * mechanisms, so two components rather than one with a mode flag.
 */

/**
 * The Replace link's label while a file is on its way: the upload percentage,
 * then — once every byte is sent and DS has not answered yet — what DS is
 * doing with it, so a full bar never reads as done.
 */
const uploadLabel = (percent: number, doneLabel: string): string =>
  percent >= 100
    ? doneLabel
    : formatMessage(printShopStrings.book.uploadingFile, {
        percent: String(percent),
      });

/** The order operators use them in: cover and manuscript print, invoice files. */
const FILE_ORDER: TicketFileKind[] = ['cover', 'manuscript', 'invoice'];

type ShipToField = 'address1' | 'address2' | 'city' | 'state' | 'zip' | 'country';

/** Form order; every line but `address2` is required (DS `UpdateShipToDto`). */
const SHIP_TO_FIELDS: ShipToField[] = [
  'address1',
  'address2',
  'city',
  'state',
  'zip',
  'country',
];
const OPTIONAL_SHIP_TO_FIELDS: ShipToField[] = ['address2'];
/** Same cap as DS `SHIP_TO_FIELD_MAX_LENGTH`. */
const SHIP_TO_FIELD_MAX_LENGTH = 200;

const shipToFormValues = (
  shipTo?: BookShipTo,
): Record<ShipToField, string> => ({
  address1: shipTo?.address1 ?? '',
  address2: shipTo?.address2 ?? '',
  city: shipTo?.city ?? '',
  state: shipTo?.state ?? '',
  zip: shipTo?.zip ?? '',
  country: shipTo?.country ?? '',
});

/** Whether the ticket's current invoice is an operator upload. */
const invoiceIsOperatorUpload = (ticket: TicketDetail): boolean =>
  (ticket.files ?? []).some(
    (file) => file.kind === 'invoice' && file.source === 'operator',
  );

/**
 * DS could not verify the operator's sign-in (expired id token): the text and
 * a button that starts a new FusionAuth sign-in.
 */
export const SignInAgainNotice = ({ onSignIn }: { onSignIn: () => void }) => (
  <p
    role="alert"
    data-testid="book-sign-in-again"
    className="mt-3 flex flex-wrap items-center gap-2 text-sm text-red-600"
  >
    {printShopStrings.book.fileRefusals.ABO_OPERATOR_UNVERIFIED}
    <button
      type="button"
      onClick={onSignIn}
      className="font-semibold underline"
    >
      {printShopStrings.book.signInAgain}
    </button>
  </p>
);

/**
 * The inline ship-to correction (abo-print-files-upload): prefilled with the
 * current address, "Regenerate the invoice" checked by default — unchecked,
 * with a note, when the current invoice is an operator upload that a
 * regenerate would replace. Save waits for DS and only then asks the page to
 * refetch — never an optimistic address.
 */
const ShipToEditForm = ({
  ticket,
  onCancel,
  onSaved,
}: {
  ticket: TicketDetail;
  onCancel: () => void;
  onSaved: () => void;
}) => {
  const [values, setValues] = useState(() => shipToFormValues(ticket.shipTo));
  const replacesUpload = invoiceIsOperatorUpload(ticket);
  const [regenerateInvoice, setRegenerateInvoice] = useState(!replacesUpload);
  const [isSaving, setIsSaving] = useState(false);
  const { getOperatorToken, signInAgain } = useOperatorToken();
  const [needsSignIn, setNeedsSignIn] = useState(false);

  const missingRequired = SHIP_TO_FIELDS.some(
    (field) =>
      !OPTIONAL_SHIP_TO_FIELDS.includes(field) && values[field].trim() === '',
  );

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (missingRequired || isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      const address2 = values.address2.trim();
      // Read (after a refresh) at Save, for this one request only.
      const token = await getOperatorToken();
      const result = await updateTicketShipTo(
        ticket.orderId,
        {
          address1: values.address1.trim(),
          ...(address2 ? { address2 } : {}),
          city: values.city.trim(),
          state: values.state.trim(),
          zip: values.zip.trim(),
          country: values.country.trim(),
          regenerateInvoice,
        },
        { token },
      );
      if (regenerateInvoice && !result.invoiceRegenerated) {
        // The address IS saved; only the invoice needs the operator's recourse.
        ToastNotification({
          type: 'failure',
          message: printShopStrings.book.shipToSavedInvoiceFailed,
        });
      } else {
        ToastNotification({
          type: 'success',
          message: regenerateInvoice
            ? printShopStrings.book.shipToSavedInvoiceRegenerated
            : printShopStrings.book.shipToSaved,
        });
      }
      onSaved();
    } catch (error) {
      if (isOperatorUnverified(error)) {
        setNeedsSignIn(true);
      } else {
        ToastNotification({ type: 'failure', message: shipToErrorMessage(error) });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      data-testid="book-ship-to-form"
      onSubmit={onSubmit}
      className="mt-2 flex flex-col gap-3"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {SHIP_TO_FIELDS.map((field) => {
          const id = `book-ship-to-${field}`;
          const required = !OPTIONAL_SHIP_TO_FIELDS.includes(field);
          return (
            <label key={field} htmlFor={id} className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-neutral-600">
                {printShopStrings.book.shipToFields[field]}
              </span>
              <input
                id={id}
                data-testid={`book-ship-to-input-${field}`}
                type="text"
                value={values[field]}
                required={required}
                maxLength={SHIP_TO_FIELD_MAX_LENGTH}
                disabled={isSaving}
                onChange={(event) => {
                  const { value } = event.target;
                  setValues((current) => ({ ...current, [field]: value }));
                }}
                className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800"
              />
            </label>
          );
        })}
      </div>
      <label className="flex items-center gap-2 text-sm text-neutral-800">
        <input
          type="checkbox"
          data-testid="book-ship-to-regenerate-invoice"
          checked={regenerateInvoice}
          disabled={isSaving}
          onChange={(event) => setRegenerateInvoice(event.target.checked)}
        />
        {printShopStrings.book.shipToRegenerateInvoice}
      </label>
      {replacesUpload && (
        <p
          data-testid="book-ship-to-regenerate-replaces-upload"
          className="-mt-2 text-xs text-amber-700"
        >
          {printShopStrings.book.shipToRegenerateReplacesUpload}
        </p>
      )}
      {needsSignIn && <SignInAgainNotice onSignIn={signInAgain} />}
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          data-testid="book-ship-to-cancel"
          disabled={isSaving}
          onClick={onCancel}
          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-semibold text-neutral-700 disabled:opacity-60"
        >
          {printShopStrings.book.shipToCancel}
        </button>
        <button
          type="submit"
          data-testid="book-ship-to-save"
          disabled={missingRequired || isSaving}
          className="rounded-md bg-primary-500 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {isSaving
            ? printShopStrings.book.shipToSaving
            : printShopStrings.book.shipToSave}
        </button>
      </div>
    </form>
  );
};

export const BookOrderFacts = ({
  ticket,
  onShipToChanged,
}: {
  ticket: TicketDetail;
  /** Called after a saved ship-to so the page refetches the ticket. */
  onShipToChanged?: () => void;
}) => {
  const [isEditingShipTo, setIsEditingShipTo] = useState(false);
  const shipTo = formatShipTo(ticket.shipTo);
  const addressMissing = Boolean(ticket.shipToMissing) || shipTo === null;
  // Same rule as Replace / Restore: a DS that sends no `editable` is read-only.
  const canEditShipTo = Boolean(ticket.editable);

  return (
    <section
      data-testid="book-order-facts"
      className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
    >
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <dt className="text-xs font-bold uppercase tracking-wider text-neutral-400">
            {printShopStrings.book.orderNumber}
          </dt>
          <dd
            data-testid="book-order-number"
            className="mt-1 font-mono text-sm font-semibold text-neutral-800"
          >
            {ticket.afyOrderNumber ?? '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-bold uppercase tracking-wider text-neutral-400">
            {printShopStrings.book.copies}
          </dt>
          <dd
            data-testid="book-copies"
            className="mt-1 text-sm font-semibold text-neutral-800"
          >
            {printShopStrings.book.copiesUnit(ticket.copies ?? ticket.quantity)}
          </dd>
        </div>
        <div className={isEditingShipTo ? 'sm:col-span-3' : 'sm:col-span-1'}>
          <dt className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-400">
            {printShopStrings.book.shipTo}
            {canEditShipTo && !isEditingShipTo && (
              <button
                type="button"
                data-testid="book-ship-to-edit"
                aria-label={printShopStrings.book.editShipToAriaLabel}
                onClick={() => setIsEditingShipTo(true)}
                className="text-xs font-semibold normal-case tracking-normal text-primary-600 hover:underline"
              >
                {printShopStrings.book.editShipTo}
              </button>
            )}
          </dt>
          {isEditingShipTo ? (
            <dd>
              <ShipToEditForm
                ticket={ticket}
                onCancel={() => setIsEditingShipTo(false)}
                onSaved={() => {
                  setIsEditingShipTo(false);
                  onShipToChanged?.();
                }}
              />
            </dd>
          ) : addressMissing ? (
            // Deliberately a warning, not a blank line: this order cannot ship
            // until someone finds an address, and an empty field would read as
            // "nothing to see here". The address was never guessed upstream.
            <dd
              data-testid="book-ship-to-missing"
              title={printShopStrings.book.shipToMissingHint}
              className="mt-1 flex items-start gap-1 text-sm font-semibold text-amber-700"
            >
              <HiOutlineExclamation className="mt-0.5 shrink-0" />
              {printShopStrings.book.shipToMissing}
            </dd>
          ) : (
            <dd
              data-testid="book-ship-to"
              className="mt-1 text-sm text-neutral-800"
            >
              {shipTo}
            </dd>
          )}
        </div>
      </dl>
    </section>
  );
};

const LINK_BUTTON_CLASS =
  'text-sm font-semibold text-primary-600 hover:underline disabled:opacity-60 disabled:no-underline';

const BookFileRow = ({
  orderId,
  file,
  editable,
  onRegenerate,
  isRegenerating,
  onChanged,
  onReplaced,
  onUnverified,
}: {
  orderId: string;
  file: TicketFileStatus;
  /** The ticket allows Replace / Restore / Regenerate (`ticket.editable`). */
  editable: boolean;
  /** Asks DS for a new generated invoice; resolves true when it succeeded. */
  onRegenerate?: () => Promise<boolean>;
  isRegenerating?: boolean;
  /** Called after a replace or restore so the page refetches the ticket. */
  onChanged?: () => void;
  /** Called after a successful replace only (the flip book reminder). */
  onReplaced?: () => void;
  /** DS could not verify the operator's sign-in: the card offers "Sign in again". */
  onUnverified?: () => void;
}) => {
  const [isPreparing, setIsPreparing] = useState(false);
  const [failed, setFailed] = useState(false);
  // `null` when idle; the upload progress (0–100) while a file is sent.
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [confirmingRestore, setConfirmingRestore] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { getOperatorToken } = useOperatorToken();

  /**
   * A fresh URL PER CLICK — never cached (ADR-006). A stored one expires, so it
   * would rot on exactly the tickets that sit longest in the queue, and it is a
   * bearer credential that should not outlive the click.
   */
  const onDownload = async () => {
    setIsPreparing(true);
    setFailed(false);
    try {
      const { url } = await fetchTicketFileDownload(orderId, file.kind);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setFailed(true);
    } finally {
      setIsPreparing(false);
    }
  };

  const label = printShopStrings.book.fileLabels[file.kind];
  const isOperatorFile = file.source === 'operator';
  const isUploading = uploadPercent !== null;
  // Only the invoice can be produced again, and only on an editable ticket:
  // DS refuses a shipped, cancelled or imported order.
  const canRegenerate = file.kind === 'invoice' && editable && Boolean(onRegenerate);

  const regenerate = async () => {
    if (await onRegenerate?.()) {
      setConfirmingRegenerate(false);
    }
  };

  // Replacing an operator upload asks first, like Restore; a generated
  // invoice is simply produced again.
  const requestRegenerate = () => {
    if (isOperatorFile) {
      setConfirmingRegenerate(true);
      return;
    }
    void regenerate();
  };

  const onFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    // Cleared so picking the same file again still fires a change.
    event.target.value = '';
    if (!picked) {
      return;
    }
    const clientError = printFileClientError(picked);
    if (clientError) {
      ToastNotification({ type: 'failure', message: clientError });
      return;
    }
    setUploadPercent(0);
    try {
      const token = await getOperatorToken();
      await uploadTicketFile(
        orderId,
        file.kind,
        picked,
        { token },
        setUploadPercent,
      );
      ToastNotification({
        type: 'success',
        message: formatMessage(printShopStrings.book.fileReplaced, {
          kind: label,
        }),
      });
      onReplaced?.();
      onChanged?.();
    } catch (error) {
      if (isOperatorUnverified(error)) {
        onUnverified?.();
        return;
      }
      ToastNotification({
        type: 'failure',
        message: printFileErrorMessage(
          error,
          printShopStrings.book.uploadFailed,
        ),
      });
    } finally {
      setUploadPercent(null);
    }
  };

  const onRestore = async () => {
    setIsRestoring(true);
    try {
      const token = await getOperatorToken();
      await restoreTicketFile(orderId, file.kind, { token });
      setConfirmingRestore(false);
      ToastNotification({
        type: 'success',
        message: formatMessage(printShopStrings.book.fileRestored, {
          kind: printShopStrings.book.fileNouns[file.kind],
        }),
      });
      onChanged?.();
    } catch (error) {
      if (isOperatorUnverified(error)) {
        onUnverified?.();
        return;
      }
      ToastNotification({
        type: 'failure',
        message: printFileErrorMessage(
          error,
          printShopStrings.book.restoreFailed,
        ),
      });
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div
      data-testid={`book-file-${file.kind}`}
      className="flex flex-col gap-2 rounded-lg border border-neutral-200 px-4 py-3"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="flex flex-col">
          <span className="text-sm font-semibold text-neutral-800">{label}</span>
          {/*
            Who replaced the generated file shows even on a closed ticket: the
            print shop must know it is not printing what the book builder made.
          */}
          {isOperatorFile && (
            <span
              data-testid={`book-file-uploaded-${file.kind}`}
              className="text-xs text-neutral-500"
            >
              {formatMessage(printShopStrings.book.uploadedBy, {
                email: file.uploadedBy ?? '—',
                date: formatDateTime(file.uploadedAt),
              })}
              {editable && file.canRestore && !confirmingRestore && (
                <>
                  {' · '}
                  <button
                    type="button"
                    data-testid={`book-file-restore-${file.kind}`}
                    onClick={() => setConfirmingRestore(true)}
                    className={LINK_BUTTON_CLASS}
                  >
                    {printShopStrings.book.restoreGenerated}
                  </button>
                </>
              )}
            </span>
          )}
        </span>
        <span className="flex items-center gap-3">
          {editable && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf"
                hidden
                data-testid={`book-file-input-${file.kind}`}
                onChange={onFilePicked}
              />
              <button
                type="button"
                data-testid={`book-file-replace-${file.kind}`}
                disabled={isUploading || isRestoring}
                onClick={() => fileInput.current?.click()}
                className={LINK_BUTTON_CLASS}
              >
                {uploadPercent !== null
                  ? uploadLabel(uploadPercent, printShopStrings.book.savingFile)
                  : printShopStrings.book.replaceFile}
              </button>
            </>
          )}
          {/*
            An existing invoice can be produced again too: after a ship-to
            change whose invoice failed, or to drop an operator upload.
          */}
          {canRegenerate && file.available && !confirmingRegenerate && (
            <button
              type="button"
              data-testid="book-file-regenerate-invoice-link"
              disabled={isRegenerating || isUploading || isRestoring}
              onClick={requestRegenerate}
              className={LINK_BUTTON_CLASS}
            >
              {isRegenerating
                ? printShopStrings.book.regeneratingInvoice
                : printShopStrings.book.regenerateExistingInvoice}
            </button>
          )}
          {file.available ? (
            <button
              type="button"
              data-testid={`book-file-download-${file.kind}`}
              disabled={isPreparing}
              onClick={onDownload}
              className="flex items-center gap-2 rounded-md border border-primary-500 px-3 py-1.5 text-sm font-semibold text-primary-600 disabled:opacity-60"
            >
              <HiOutlineDownload />
              {isPreparing
                ? printShopStrings.book.downloading
                : printShopStrings.book.download}
            </button>
          ) : (
            // A state the operator can read, never a dead link. `not-rendered-yet`
            // is the NORMAL early condition of a fresh order, not a failure.
            <span className="flex items-center gap-3">
              <span
                data-testid={`book-file-unavailable-${file.kind}`}
                className="text-sm text-neutral-500"
              >
                {printShopStrings.book.unavailable[file.reason ?? 'not-generated']}
              </span>
              {/*
                Recourse, not just a diagnosis. An invoice that was never produced
                is recoverable — the cover and manuscript are not, they are the book
                builder's to render.
              */}
              {canRegenerate && !confirmingRegenerate && (
                <button
                  type="button"
                  data-testid="book-file-regenerate-invoice"
                  disabled={isRegenerating}
                  onClick={requestRegenerate}
                  className="rounded-md border border-primary-500 px-3 py-1.5 text-sm font-semibold text-primary-600 disabled:opacity-60"
                >
                  {isRegenerating
                    ? printShopStrings.book.regeneratingInvoice
                    : printShopStrings.book.regenerateInvoice}
                </button>
              )}
            </span>
          )}
          {failed && (
            <span
              data-testid={`book-file-error-${file.kind}`}
              className="text-sm text-red-600"
            >
              {printShopStrings.book.downloadFailed}
            </span>
          )}
        </span>
      </div>
      {confirmingRegenerate && (
        <div
          data-testid="book-file-regenerate-confirm"
          className="flex flex-wrap items-center gap-3 rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-800"
        >
          <span
            data-testid="book-file-regenerate-warning"
            className="text-amber-700"
          >
            {printShopStrings.book.regenerateReplacesUpload}
          </span>
          <button
            type="button"
            data-testid="book-file-regenerate-confirm-button"
            disabled={isRegenerating}
            onClick={() => void regenerate()}
            className="rounded-md border border-primary-500 px-3 py-1 text-sm font-semibold text-primary-600 disabled:opacity-60"
          >
            {isRegenerating
              ? printShopStrings.book.regeneratingInvoice
              : printShopStrings.book.regenerateConfirmButton}
          </button>
          <button
            type="button"
            data-testid="book-file-regenerate-cancel"
            disabled={isRegenerating}
            onClick={() => setConfirmingRegenerate(false)}
            className={LINK_BUTTON_CLASS}
          >
            {printShopStrings.book.regenerateCancelButton}
          </button>
        </div>
      )}
      {confirmingRestore && (
        <div
          data-testid={`book-file-restore-confirm-${file.kind}`}
          className="flex flex-wrap items-center gap-3 rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-800"
        >
          <span>
            {formatMessage(printShopStrings.book.restoreConfirm, {
              kind: printShopStrings.book.fileNouns[file.kind],
            })}
          </span>
          <button
            type="button"
            data-testid={`book-file-restore-confirm-button-${file.kind}`}
            disabled={isRestoring}
            onClick={onRestore}
            className="rounded-md border border-primary-500 px-3 py-1 text-sm font-semibold text-primary-600 disabled:opacity-60"
          >
            {isRestoring
              ? printShopStrings.book.restoring
              : printShopStrings.book.restoreConfirmButton}
          </button>
          <button
            type="button"
            data-testid={`book-file-restore-cancel-${file.kind}`}
            disabled={isRestoring}
            onClick={() => setConfirmingRestore(false)}
            className={LINK_BUTTON_CLASS}
          >
            {printShopStrings.book.restoreCancelButton}
          </button>
        </div>
      )}
    </div>
  );
};

type BookLinkKind = 'flipBook' | 'leadSite';

/** The two link rows, after the print files (order-flipbook-links). */
const LINK_ORDER: BookLinkKind[] = ['flipBook', 'leadSite'];

const LINK_TEST_IDS: Record<BookLinkKind, string> = {
  flipBook: 'flip-book',
  leadSite: 'lead-site',
};

const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Only a web page is ever rendered as a link. The URLs come from stored member
 * data, so anything else (`javascript:`, `data:`, garbage) is shown as
 * unavailable rather than handed to the browser.
 */
const toSafeHref = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    return SAFE_LINK_PROTOCOLS.has(new URL(url).protocol) ? url : null;
  } catch {
    return null;
  }
};

const linkUrlOf = (
  links: TicketBookLinks | undefined,
  kind: BookLinkKind,
): string | null | undefined =>
  kind === 'flipBook' ? links?.flipBookUrl : links?.leadSiteUrl;

/**
 * "Replaced by … · date · state" under the Flip book label, after a replace
 * from Print Shop; `null` otherwise. An `unknown` state is left out.
 */
const flipBookSubline = (links?: TicketBookLinks): string | null => {
  const replacement = links?.flipBookReplacement;
  if (!replacement) {
    return null;
  }
  const replaced = formatMessage(printShopStrings.book.flipBookReplacedBy, {
    email: replacement.replacedBy,
    date: formatDateTime(replacement.replacedAt),
  });
  const status = links?.flipBookStatus;
  return status && status !== 'unknown'
    ? `${replaced} · ${printShopStrings.book.flipBookStatuses[status]}`
    : replaced;
};

/**
 * A public page about the book — the flip book or the member's lead-capture
 * site — opened in a new tab. Unlike `BookFileRow` there is nothing to sign:
 * these are the same pages the member's DMP links to.
 *
 * The flip book row also carries its replace (abo-print-files-flipbook-sync):
 * the Replace link, the Out of date badge and who last replaced it.
 */
const BookLinkRow = ({
  kind,
  links,
  onReplace,
  uploadPercent = null,
}: {
  kind: BookLinkKind;
  links?: TicketBookLinks;
  /** Opens the flip book picker; absent when the ticket cannot replace it. */
  onReplace?: () => void;
  /** The flip book upload progress (0–100), `null` when idle. */
  uploadPercent?: number | null;
}) => {
  const testId = LINK_TEST_IDS[kind];
  const href = toSafeHref(linkUrlOf(links, kind));
  const isPreview = kind === 'flipBook' && links?.flipBookSource === 'preview';
  // "We could not tell" is never shown as "there is none". An older DS sends
  // no `links` at all, which reads as not available.
  const absentLabel = links?.lookupFailed
    ? printShopStrings.book.linkLookupFailed
    : printShopStrings.book.linkUnavailable[kind];
  const isFlipBook = kind === 'flipBook';
  // Only a flip book that exists can be out of date.
  const outOfDate =
    isFlipBook && Boolean(links?.flipBookId) && links?.flipBookOutOfDate === true;
  const subline = isFlipBook ? flipBookSubline(links) : null;
  const isUploading = uploadPercent !== null;

  return (
    <div
      data-testid={`book-link-${testId}`}
      className="flex items-center justify-between gap-4 rounded-lg border border-neutral-200 px-4 py-3"
    >
      <span className="flex flex-col">
        <span className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
          {printShopStrings.book.linkLabels[kind]}
          {outOfDate && (
            <span
              data-testid={`book-link-out-of-date-${testId}`}
              title={printShopStrings.book.flipBookOutOfDateHint}
              className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700"
            >
              {printShopStrings.book.flipBookOutOfDate}
            </span>
          )}
        </span>
        {subline && (
          <span
            data-testid={`book-link-replaced-${testId}`}
            className="text-xs text-neutral-500"
          >
            {subline}
          </span>
        )}
      </span>
      <span className="flex items-center gap-3">
        {onReplace && (
          <button
            type="button"
            data-testid={`book-link-replace-${testId}`}
            disabled={isUploading}
            onClick={onReplace}
            className={LINK_BUTTON_CLASS}
          >
            {uploadPercent !== null
              ? uploadLabel(uploadPercent, printShopStrings.book.updatingFlipBook)
              : printShopStrings.book.replaceFile}
          </button>
        )}
        {href ? (
          <span className="flex items-center gap-3">
            {isPreview && (
            <span
              data-testid={`book-link-preview-hint-${testId}`}
              className="text-sm text-neutral-500"
            >
                {printShopStrings.book.flipBookPreviewHint}
              </span>
            )}
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`book-link-open-${testId}`}
              className="flex items-center gap-2 rounded-md border border-primary-500 px-3 py-1.5 text-sm font-semibold text-primary-600"
            >
              <HiOutlineExternalLink />
              {printShopStrings.book.openLink}
            </a>
          </span>
        ) : (
          <span
            data-testid={`book-link-unavailable-${testId}`}
            className="text-sm text-neutral-500"
          >
            {absentLabel}
          </span>
        )}
      </span>
    </div>
  );
};

/**
 * Right after the cover or manuscript is replaced: the flip book still shows
 * the previous one. Offers the same flip book picker, or Dismiss.
 */
const FlipBookReminder = ({
  kind,
  isUploading,
  onReplace,
  onDismiss,
}: {
  kind: TicketFileKind;
  isUploading: boolean;
  onReplace: () => void;
  onDismiss: () => void;
}) => (
  <div
    role="status"
    data-testid="book-flip-book-reminder"
    className="flex flex-wrap items-center gap-3 rounded-md bg-orange-50 px-3 py-2 text-sm text-neutral-800"
  >
    <span className="text-orange-800">
      {formatMessage(printShopStrings.book.flipBookReminder, {
        kind: printShopStrings.book.fileNouns[kind],
      })}
    </span>
    <button
      type="button"
      data-testid="book-flip-book-reminder-replace"
      disabled={isUploading}
      onClick={onReplace}
      className="rounded-md border border-primary-500 px-3 py-1 text-sm font-semibold text-primary-600 disabled:opacity-60"
    >
      {printShopStrings.book.flipBookReminderReplace}
    </button>
    <button
      type="button"
      data-testid="book-flip-book-reminder-dismiss"
      onClick={onDismiss}
      className={LINK_BUTTON_CLASS}
    >
      {printShopStrings.book.flipBookReminderDismiss}
    </button>
  </div>
);

/** The print files whose replace leaves the flip book behind. */
const FLIP_BOOK_SOURCE_KINDS: TicketFileKind[] = ['cover', 'manuscript'];

export const BookPrintFiles = ({
  ticket,
  onFilesChanged,
}: {
  ticket: TicketDetail;
  /** Called after any file write so the page refetches the ticket. */
  onFilesChanged?: () => void;
}) => {
  const byKind = new Map((ticket.files ?? []).map((f) => [f.kind, f]));
  const [isRegenerating, setIsRegenerating] = useState(false);
  // ABO's text for the last failed regenerate, or null.
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const { getOperatorToken, signInAgain } = useOperatorToken();
  // DS could not verify the operator's sign-in on the last file action.
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const flipBookInput = useRef<HTMLInputElement>(null);
  // `null` when idle; the flip book upload progress (0–100) while it is sent.
  const [flipBookUploadPercent, setFlipBookUploadPercent] = useState<
    number | null
  >(null);
  // The print file whose replace just succeeded on this page view, for the
  // flip book reminder; `null` when none, or after Dismiss.
  const [reminderKind, setReminderKind] = useState<TicketFileKind | null>(null);

  const onRegenerate = async (): Promise<boolean> => {
    setIsRegenerating(true);
    setRegenerateError(null);
    try {
      const token = await getOperatorToken();
      await regenerateTicketInvoice(ticket.orderId, { token });
      setNeedsSignIn(false);
      ToastNotification({
        type: 'success',
        message: printShopStrings.book.regenerateInvoiceDone,
      });
      // Let the page refetch: the file's availability is resolved server-side
      // at read time, so the row updates from the source rather than from an
      // optimistic guess here.
      onFilesChanged?.();
      return true;
    } catch (error) {
      if (isOperatorUnverified(error)) {
        setNeedsSignIn(true);
        return false;
      }
      setRegenerateError(
        printFileErrorMessage(
          error,
          printShopStrings.book.regenerateInvoiceFailed,
        ),
      );
      return false;
    } finally {
      setIsRegenerating(false);
    }
  };

  // An older DS sends no `editable`: read as closed, so nothing is offered
  // that DS would refuse.
  const editable = ticket.editable === true;
  // DS found the member's published flip book; without one there is nothing
  // to replace (DS would answer FLIPBOOK_NOT_FOUND).
  const canReplaceFlipBook = editable && Boolean(ticket.links?.flipBookId);
  const showReminder =
    reminderKind !== null &&
    canReplaceFlipBook &&
    ticket.links?.flipBookOutOfDate === true;

  const pickFlipBook = () => flipBookInput.current?.click();

  const onFlipBookPicked = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const picked = event.target.files?.[0];
    // Cleared so picking the same file again still fires a change.
    event.target.value = '';
    if (!picked) {
      return;
    }
    const clientError = printFileClientError(picked);
    if (clientError) {
      ToastNotification({ type: 'failure', message: clientError });
      return;
    }
    setFlipBookUploadPercent(0);
    try {
      const token = await getOperatorToken();
      await replaceTicketFlipBook(
        ticket.orderId,
        picked,
        { token },
        setFlipBookUploadPercent,
      );
      setNeedsSignIn(false);
      setReminderKind(null);
      ToastNotification({
        type: 'success',
        message: printShopStrings.book.flipBookReplaced,
      });
      onFilesChanged?.();
    } catch (error) {
      if (isOperatorUnverified(error)) {
        setNeedsSignIn(true);
        return;
      }
      ToastNotification({
        type: 'failure',
        message: flipBookReplaceErrorMessage(error),
      });
    } finally {
      setFlipBookUploadPercent(null);
    }
  };

  return (
    <section
      data-testid="book-print-files"
      className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm lg:col-span-2"
    >
      <h2 className="text-xs font-bold uppercase tracking-wider text-neutral-400">
        {printShopStrings.book.printFilesTitle}
      </h2>
      <p className="mb-4 mt-1 text-sm font-semibold text-neutral-800">
        {ticket.asset.title}
      </p>
      <div className="flex flex-col gap-2">
        {FILE_ORDER.map((kind) => (
          <React.Fragment key={kind}>
            <BookFileRow
              orderId={ticket.orderId}
              // An older DS deploy may not report every kind; treat a missing
              // entry as "never produced" rather than rendering nothing at all,
              // so the row count is stable and the gap is visible.
              file={byKind.get(kind) ?? { kind, available: false }}
              editable={editable}
              onRegenerate={kind === 'invoice' ? onRegenerate : undefined}
              isRegenerating={isRegenerating}
              onChanged={() => {
                setNeedsSignIn(false);
                onFilesChanged?.();
              }}
              onReplaced={
                FLIP_BOOK_SOURCE_KINDS.includes(kind)
                  ? () => setReminderKind(kind)
                  : undefined
              }
              onUnverified={() => setNeedsSignIn(true)}
            />
            {/*
              Shown once the refetched ticket confirms the flip book is behind,
              so a replace DS already accounted for asks nothing.
            */}
            {showReminder && reminderKind === kind && (
              <FlipBookReminder
                kind={kind}
                isUploading={flipBookUploadPercent !== null}
                onReplace={pickFlipBook}
                onDismiss={() => setReminderKind(null)}
              />
            )}
          </React.Fragment>
        ))}
        {LINK_ORDER.map((kind) => (
          <BookLinkRow
            key={kind}
            kind={kind}
            links={ticket.links}
            onReplace={
              kind === 'flipBook' && canReplaceFlipBook ? pickFlipBook : undefined
            }
            uploadPercent={kind === 'flipBook' ? flipBookUploadPercent : null}
          />
        ))}
      </div>
      {canReplaceFlipBook && (
        // One picker for the row's Replace and the reminder's button.
        <input
          ref={flipBookInput}
          type="file"
          accept="application/pdf"
          hidden
          data-testid="book-flip-book-input"
          onChange={onFlipBookPicked}
        />
      )}
      {editable && (
        <p
          data-testid="book-print-files-footnote"
          className="mt-3 text-xs text-neutral-500"
        >
          {printShopStrings.book.replaceFootnote}
        </p>
      )}
      {canReplaceFlipBook && (
        <p
          data-testid="book-flip-book-footnote"
          className="mt-1 text-xs text-neutral-500"
        >
          {printShopStrings.book.flipBookFootnote}
        </p>
      )}
      {needsSignIn && <SignInAgainNotice onSignIn={signInAgain} />}
      {regenerateError && (
        <p
          data-testid="book-invoice-regenerate-error"
          className="mt-3 text-sm text-red-600"
        >
          {regenerateError}
        </p>
      )}
    </section>
  );
};
